import { html, nothing, type PropertyValues } from 'lit';
import { keyed } from 'lit/directives/keyed.js';
import { repeat } from 'lit/directives/repeat.js';
import { customElement, property, state } from 'lit/decorators.js';
import { BasePage } from '@vitesse/base-ui/components/base-page';
import { statusBadge } from '@vitesse/base-ui/components/templates';
import { button, iconButton } from '@vitesse/base-ui/components/base-buttons';
import './vitesse-widget-config-dialog';
import type { SettingSpec, WidgetAction, WidgetMeta } from '../../dashboards/runtime/sdk';
import type { VitesseWidgetHost } from '../../dashboards/widget-host';
import { Dashboards } from '@vitesse/proto/vitesse/v1/dashboard_pb.js';
import '@vitesse/base-ui/elements/vitesse-gated';
import '@vitesse/base-ui/elements/vitesse-confirm-dialog';
import './vitesse-dashboard-share-dialog';
import './vitesse-edit-dashboard-dialog';
import { readMetrics, dropMetrics, boardEverLive } from '../../dashboards/board-metrics';
import { t } from '@vitesse/base-ui/i18n';
import '@vitesse/base-ui/elements/vitesse-editable-description';
import type { DashboardManager } from '../managers/dashboard-manager';
import type { BackendClient } from '../bridge/backend-client';
import type { Dashboard, DashboardTile } from '../../core/domain/dashboard';
import type { ConnState, ControllerManager } from '../managers/controller-manager';
import type { ModelManager } from '../managers/model-manager';
import type { IntegrationManager } from '../managers/integration-manager';
import type { CapabilityGrant } from '../../dashboards/runtime/capability';
import { DENY_ALL } from '../../dashboards/runtime/capability';
import './vitesse-panel-grid';
import type { PanelDef, PanelLayoutItem } from './vitesse-panel-grid';
import '../../dashboards/widget-host';
import './dashboard-activity';
import type { PillTab } from '@vitesse/base-ui/components/templates';

/*
 * <vitesse-dashboard-detail> - one dashboard, rendered.
 *
 * Composed the same way the model workspace is: BasePage supplies the header
 * chrome, `.vg-wrapper` the frame, and <vitesse-panel-grid> the canvas, with
 * one light-DOM child per tile projected through its slot. Nothing here is a
 * new pattern - a dashboard IS the model workspace with a different set of
 * tiles, which is what the panel grid was built for.
 *
 * The one thing this page owns that others do not: every tile is a
 * <vitesse-widget-host>, which means every tile is untrusted code in its own
 * frame. This page never touches a widget's source, never resolves its values,
 * and never decides what it may read - it hands each host a grant and gets out
 * of the way.
 */
/** A model tile still titled by the catalog (or by nothing, or by a bare model id) is titled
 *  by the model it is bound to: "<model> commands", "<model> statechart". */
function tileTitle(tile: DashboardTile): string | undefined {
  if (tile.source.startsWith('native:model-')) {
    const id = tile.config['modelId'];
    const own = tile.title && !/^Model (commands|statechart)$/i.test(tile.title) && tile.title !== id;
    if (typeof id === 'string' && id && !own) {
      return tile.source === 'native:model-commands' ? t('model.tileCommandsTitle', { id }) : t('model.tileStatechartTitle', { id });
    }
  }
  return tile.title;
}

/** A tile being published, with what its widget said about itself (absent for a tile that never came up). */
export interface TilePublish {
  tile: DashboardTile;
  meta?: WidgetMeta;
}

@customElement('vitesse-dashboard-detail')
export class VitesseDashboardDetail extends BasePage {
  @property({ attribute: false }) dashboards?: DashboardManager;
  @property({ attribute: false }) backend?: BackendClient;
  /** Only so a badge can name a controller rather than show its id. A board
   *  watching "Press line 2" says something; one watching
   *  "mock-938637234be8" says the same thing in a form nobody reads. */
  @property({ attribute: false }) controllers?: ControllerManager;
  /** Only so a source heading names a model or an integration rather than
   *  showing its id. */
  @property({ attribute: false }) models?: ModelManager;
  @property({ attribute: false }) integrations?: IntegrationManager;
  @property({ type: String }) gatewayId = '';
  /** Shown as the back label, matching the other detail pages. */
  @property({ type: String }) gatewayName = '';
  @property({ type: String }) dashboardId = '';
  /** The RAW fleet roster + our cloud servers + org names — the map widget renders
   *  exactly what the fleet app's map renders (server-scoped: super-admins see all). */
  @property({ attribute: false }) mapFleet: unknown[] = [];
  @property({ attribute: false }) mapServers: unknown[] = [];
  @property({ attribute: false }) orgNames: Map<string, string> = new Map();
  /** The signed-in user, forwarded to the trail so entries written before the
   *  gateway recorded names still read as a person. */
  @property({ type: String }) selfId = '';
  @property({ type: String }) selfName = '';
  /*
   * KIOSK — rotate through the gateway's boards on an interval, the way a big
   * screen cycles what a walk-past should see. The shell owns the flag (the
   * rotation is navigation); this page owns the timer, because it knows the
   * roster and the current position. Rotation order is the roster's, wrap at
   * the end; the interval is long enough to actually read a board.
   */
  @property({ type: Boolean }) kiosk = false;
  /** Whether a tile's widget source is already on a shelf (built-in, marketplace, or published).
   *  Supplied by the shell, which holds the shelves; a tile whose source is on none gets a publish
   *  button. Absent: no tile does. */
  @property({ attribute: false }) knownSource?: (source: string) => boolean;
  /** The delete confirmation, and what the gateway said if it refused. */
  @state() private confirmDelete = false;
  @state() private sharing = false;
  @state() private editing = false;
  @state() private deleting = false;
  @state() private deleteError = '';
  private kioskTimer = 0;
  /**
   * What the tiles on this page may do. The app passes a session grant; a
   * shared page passes one derived from a snapshot. This element does not care
   * which, and that is what lets the same page serve both.
   */
  @property({ attribute: false }) grant: CapabilityGrant = DENY_ALL;
  /**
   * The gateway's authoritative tri-state, from the same derivation the home
   * card, the tree and the gateway page use. A dashboard is gateway-owned data
   * rendering gateway-owned values, so it rides the same seam as every other
   * entity: no connection, no dashboard. Anything else would show an operator a
   * board of numbers with no way to tell that none of them are current, which
   * on a wall display is the most dangerous thing this page could do.
   */
  @property({ type: String }) connState: ConnState = 'disconnected';

  @state() private body?: Dashboard;
  @state() private missing = false;
  @state() private layout?: PanelLayoutItem[];
  /** Tiles whose widget faulted, so the page can say so rather than show a hole. */
  @state() private faulted = new Set<string>();
  /** What each tile's widget declared for the base settings dialog (from its `ready` meta). */
  @state() private settings = new Map<string, { schema: SettingSpec[]; own: boolean }>();
  /** The tile whose settings dialog is open. */
  @state() private settingsTile = '';
  /** The buttons each tile's widget declared for the tile bar (a chart re-announces as its toggles change). */
  @state() private actions = new Map<string, WidgetAction[]>();
  /** Click-to-edit on the description, the same gesture a controller uses. */
  @state() private tab: 'board' | 'activity' = 'board';
  /** Bumped after a save so the activity view re-reads, rather than polling for
   *  a change this page already knows about. */
  @state() private activityRevision = 0;
  /** The live rate + lag badge, refreshed on a slow timer while the page is
   *  mounted. Reading a module-level meter on a 1s tick, rather than pushing
   *  per-batch updates into Lit state — the badge needs a heartbeat, not a
   *  render per value batch. */
  @state() private metrics?: { perSecond: number; lagMs: number };
  /*
   * THE BACKGROUND'S OBJECT URL.
   *
   * The bytes arrive with the body — the background is part of the document, not
   * something it points at — so there is nothing to fetch and nothing that can
   * fail. What there IS is a blob to release: an object URL pins its blob for the
   * life of the DOCUMENT, so a person clicking through twenty boards would hold
   * twenty pictures until the tab closed.
   *
   * Rebuilt whenever the body changes and revoked on the way out. Keyed on the
   * bytes rather than on the board id, because saving a new background on the
   * SAME board must replace the picture.
   */
  private bgUrl = '';
  private bgBytes?: Uint8Array;
  private metricsTimer = 0;
  /** When THIS board was opened — the stale badge's grace anchor. A board that
   *  just opened has not had a chance to receive anything yet; calling it stale
   *  in that first window would be an accusation, not a measurement. */
  private openedAtMs = Date.now();
  /** How long a freshly-opened bound board may show no data before the badge is
   *  allowed to say so. Comfortably past first-connect + first-snapshot. */
  private static readonly STALE_GRACE_MS = 15_000;
  private retryTimer = 0;

  /*
   * A colleague's save, noticed live. The roster stream already delivers the
   * new digest; without this listener the page held its copy indefinitely and
   * the Logs tab showed a trail frozen at open time — "live" only for one's
   * own edits, which is the least useful half.
   */
  private seenDigest = '';
  private readonly onRosterChange = (): void => {
    const digest = this.dashboards?.get(this.dashboardId)?.digest ?? '';
    if (!digest || digest === this.seenDigest) return;
    const first = this.seenDigest === '';
    this.seenDigest = digest;
    if (first) return; // the digest we opened at is not a change
    this.activityRevision++; // the trail gained entries
    if (this.ownSaves > 0) {
      // Our own write coming back. The tiles on screen already show it, so take
      // the fresh body in place rather than tearing the board down and restarting
      // every tile (a chart would lose its samples, its pause, its cursors).
      this.ownSaves--;
      void this.refreshBody();
      return;
    }
    void this.loadBody(); // and the board itself moved under us
  };

  /** Writes this page made that have not yet echoed back through the roster. */
  private ownSaves = 0;

  /**
   * A board save of our own, ONE AT A TIME.
   *
   * Two edits in the same instant (a tile binding a model writes its config and then its
   * title) used to go out as two saves on the same base. Both passed the gateway's base
   * check, their body and summary writes interleaved, and the board came to rest with a
   * summary from one save and a body from the other — after which every save was refused
   * as a conflict. So a save is a MUTATION queued behind the previous one: it reads the
   * board as it is when its turn comes, saves, and takes the fresh body (and digest) before
   * the next mutation runs. Counted before the RPC so the roster's echo, however early, is
   * ours.
   */
  private saveChain: Promise<void> = Promise.resolve();
  private saveOwn(mutate: (board: Dashboard) => Dashboard | null): void {
    this.saveChain = this.saveChain.then(async () => {
      const board = this.body;
      if (!board || !this.dashboards) return;
      const next = mutate(board);
      if (!next) return;
      this.body = next;
      this.ownSaves++;
      const r = await this.dashboards.save(next, board.digest);
      if (!r.ok) {
        this.ownSaves = Math.max(0, this.ownSaves - 1);
        return;
      }
      await this.refreshBody(); // the digest the next save must base on
    });
  }

  private async refreshBody(): Promise<void> {
    if (!this.dashboards || !this.dashboardId) return;
    const requested = this.dashboardId;
    let found: Dashboard | undefined;
    try {
      found = await this.dashboards.body(requested);
    } catch {
      return; // the next roster change reloads properly
    }
    if (this.dashboardId !== requested || !found) return;
    this.body = found;
    this.layout = found.tiles.map((tile) => ({ id: tile.id, ...tile.layout }));
  }

  private static readonly KIOSK_INTERVAL_MS = 20_000;

  private kioskNext = (): void => {
    const all = this.dashboards?.all ?? [];
    if (all.length < 2) return; // one board rotates to itself; leave it alone
    const i = all.findIndex((d) => d.id === this.dashboardId);
    const next = all[(i + 1) % all.length];
    this.dispatchEvent(
      new CustomEvent('navigate', {
        detail: { view: 'dashboard', gatewayId: this.gatewayId, dashboardId: next.id },
        bubbles: true,
        composed: true,
      }),
    );
  };

  protected willUpdate(changed: Map<string, unknown>): void {
    super.willUpdate(changed as never); // Lit's own hook — BasePage does not override it, Lit does
    if (changed.has('kiosk')) {
      clearInterval(this.kioskTimer);
      if (this.kiosk) this.kioskTimer = setInterval(this.kioskNext, VitesseDashboardDetail.KIOSK_INTERVAL_MS) as unknown as number;
    }
  }

  connectedCallback(): void {
    super.connectedCallback();
    if (this.kiosk) this.kioskTimer = setInterval(this.kioskNext, VitesseDashboardDetail.KIOSK_INTERVAL_MS) as unknown as number;
    // The listener itself is (re)attached in `updated`, where the manager property lands; here it
    // only covers a re-insert of an element that already has one.
    this.dashboards?.addEventListener('change', this.onRosterChange);
    this.onRosterChange(); // seed seenDigest with the current state
    this.metricsTimer = setInterval(() => {
      this.metrics = readMetrics(`${this.gatewayId}/${this.dashboardId}`);
    }, 1000) as unknown as number;
  }
  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.dashboards?.removeEventListener('change', this.onRosterChange);
    clearTimeout(this.layoutSaveTimer);
    clearInterval(this.kioskTimer);
    clearInterval(this.metricsTimer);
    clearTimeout(this.retryTimer);
    dropMetrics(`${this.gatewayId}/${this.dashboardId}`);
    this.releaseBackground();
  }

  private releaseBackground(): void {
    if (this.bgUrl) URL.revokeObjectURL(this.bgUrl);
    this.bgUrl = '';
    this.bgBytes = undefined;
  }

  /**
   * The stage's inline custom properties, or nothing when this board has no
   * picture.
   *
   * The STYLESHEET never learns what a fit is: it reads `--board-bg-size` and
   * `--board-bg-repeat`, which are decided here from the stored enum. One place
   * knows the mapping — this one — rather than a map in the wire layer and a
   * second, silently-drifting copy in CSS.
   */
  private backgroundStyle(): string | typeof nothing {
    const bg = this.body?.background;
    if (!bg) {
      this.releaseBackground();
      return nothing;
    }
    if (bg.bytes !== this.bgBytes) {
      this.releaseBackground();
      this.bgBytes = bg.bytes;
      // Sliced: a view into a larger buffer would blob the WHOLE buffer.
      this.bgUrl = URL.createObjectURL(new Blob([bg.bytes.slice().buffer as ArrayBuffer], { type: bg.mediaType }));
    }
    const size = bg.fit === 'contain' ? 'contain' : bg.fit === 'tile' ? 'auto' : 'cover';
    const repeat = bg.fit === 'tile' ? 'repeat' : 'no-repeat';
    // The URL is one this document just minted from its own bytes — not a string
    // from anywhere a caller could influence — so there is nothing to escape.
    return `--board-bg-image:url(${this.bgUrl});--board-bg-size:${size};--board-bg-repeat:${repeat};--board-bg-dim:${bg.dimPercent}%`;
  }

  protected updated(changed: PropertyValues<this>): void {
    if (changed.has('dashboardId')) this.openedAtMs = Date.now(); // fresh board → fresh grace window
    /*
     * THE ROSTER LISTENER FOLLOWS THE MANAGER, not the element's lifetime.
     *
     * It was attached once, in connectedCallback — and the manager arrives as a property, committed
     * after the element is already in the document. So the listener was attached to nothing, the
     * digest was never seeded, and a board edited from elsewhere (the assistant adding a tile, a
     * colleague saving) stayed as it was until the page was reloaded. Same on a gateway switch: the
     * old manager kept the listener and the new one had none.
     */
    if (changed.has('dashboards')) {
      (changed.get('dashboards') as DashboardManager | undefined)?.removeEventListener('change', this.onRosterChange);
      this.dashboards?.addEventListener('change', this.onRosterChange);
      this.seenDigest = '';
      this.onRosterChange(); // seed with what the new manager already knows
    }
    if (changed.has('dashboardId')) this.seenDigest = ''; // a different board: its first digest is the opening state
    if (changed.has('dashboardId') || changed.has('dashboards')) void this.loadBody();
  }

  private async loadBody(): Promise<void> {
    clearTimeout(this.retryTimer); // a fresh load supersedes any pending retry
    this.body = undefined;
    this.missing = false;
    this.faulted = new Set();
    if (!this.dashboards || !this.dashboardId) return;
    const requested = this.dashboardId;
    let found: Dashboard | undefined;
    try {
      found = await this.dashboards.body(requested);
    } catch {
      /*
       * UNAVAILABLE IS NOT MISSING. A cold deep-link's first fetch races the
       * transport coming up, and this page used to read that race as "no such
       * board" — a message a person believes, and acts on. So a failed ASK
       * keeps the loading state and retries on a short backoff until the
       * gateway answers (either with the board or with a real not-found).
       * The retry stops the moment the user navigates elsewhere.
       */
      if (this.dashboardId !== requested || !this.isConnected) return;
      this.retryTimer = setTimeout(() => void this.loadBody(), 1500) as unknown as number;
      return;
    }
    if (this.dashboardId !== requested) return;
    if (!found) { this.missing = true; return; }
    this.body = found;
    this.layout = found.tiles.map((tile) => ({ id: tile.id, ...tile.layout }));
  }

  private get panels(): PanelDef[] {
    return (this.body?.tiles ?? []).map((tile) => ({
      id: tile.id,
      title: tileTitle(tile),
      x: tile.layout.x, y: tile.layout.y, w: tile.layout.w, h: tile.layout.h,
      minW: 2, minH: 2,
    }));
  }

  private layoutSaveTimer = 0;
  private onLayoutChange = (e: CustomEvent<PanelLayoutItem[]>): void => {
    this.layout = e.detail;
    /*
     * A MOVE IS AN EDIT, so it persists and it shows in the Logs — "somebody
     * moved something around" was exactly the change the trail failed to
     * record while layout lived in memory only, and the next visitor found a
     * rearranged wall with a trail that swore nothing happened. Debounced: a
     * drag emits a stream of layouts and only the one the hand settles on is a
     * fact worth writing. Saved with the digest we read, so a drag over a
     * board a colleague just edited is refused rather than clobbering them.
     */
    clearTimeout(this.layoutSaveTimer);
    this.layoutSaveTimer = setTimeout(() => void this.saveLayout(), 1200) as unknown as number;
  };

  private saveLayout(): void {
    const board = this.body;
    const layout = this.layout;
    if (!board || !layout || !this.dashboards) return;
    const byId = new Map(layout.map((l) => [l.id, l]));
    const tiles = board.tiles.map((tile) => {
      const l = byId.get(tile.id);
      return l ? { ...tile, layout: { x: l.x, y: l.y, w: l.w, h: l.h } } : tile;
    });
    const moved = tiles.some((tile, i) => {
      const o = board.tiles[i].layout;
      return tile.layout.x !== o.x || tile.layout.y !== o.y || tile.layout.w !== o.w || tile.layout.h !== o.h;
    });
    if (!moved) return; // a drag that landed back where it started is not an edit
    this.saveOwn((b) => {
      const byId2 = new Map(layout.map((l) => [l.id, l]));
      return { ...b, tiles: b.tiles.map((tile) => { const l = byId2.get(tile.id); return l ? { ...tile, layout: { x: l.x, y: l.y, w: l.w, h: l.h } } : tile; }) };
    });
    // A refusal (colleague's edit landed first) is left alone: the roster
    // listener is about to reload the fresh board anyway.
  }

  /**
   * A tile changed its own settings (the chart's window, theme, thresholds; a note's text):
   * write them into that tile and save the board. Saved with the digest we
   * read, like a layout change — a colleague's edit landing first is refused
   * and the roster listener reloads the fresh board.
   */
  private onTileConfigChange = (e: Event): void => {
    const host = e.currentTarget as HTMLElement;
    const id = host.getAttribute('slot');
    const board = this.body;
    const config = (e as CustomEvent<Record<string, unknown>>).detail;
    if (!id || !board || !this.dashboards || !config) return;
    this.saveOwn((b) => ({ ...b, tiles: b.tiles.map((tile) => (tile.id === id ? { ...tile, config } : tile)) }));
  };

  /** A tile renamed itself (a model tile bound to a model takes its name). Same write as a config change. */
  private onTileTitleChange = (e: Event): void => {
    const host = e.currentTarget as HTMLElement;
    const id = host.getAttribute('slot');
    const board = this.body;
    const title = (e as CustomEvent<string>).detail;
    if (!id || !board || !this.dashboards || !title) return;
    this.saveOwn((b) => ({ ...b, tiles: b.tiles.map((tile) => (tile.id === id ? { ...tile, title } : tile)) }));
  };

  /** The tile's trash: drop it from the board and save. The grid removes the item itself. */
  private onPanelRemove = (e: Event): void => {
    const id = (e as CustomEvent<string>).detail;
    const board = this.body;
    if (!id || !board || !this.dashboards) return;
    if (!board.tiles.some((tile) => tile.id === id)) return;
    this.layout = board.tiles.filter((tile) => tile.id !== id).map((tile) => ({ id: tile.id, ...tile.layout }));
    this.saveOwn((b) => ({ ...b, tiles: b.tiles.filter((tile) => tile.id !== id) }));
  };

  /** A widget said what it takes: remember it so the tile chrome can offer a gear. */
  /** What each widget reported about itself when it came up — its own size, aspect and settings —
   *  so publishing one describes it as the widget does, not as its tile happens to be laid out. */
  private metas = new Map<string, WidgetMeta>();

  private onWidgetReady = (e: Event): void => {
    const host = e.target as HTMLElement;
    const id = host.getAttribute('slot');
    const meta = (e as CustomEvent<WidgetMeta>).detail ?? {};
    if (!id) return;
    this.metas.set(id, meta);
    const next = new Map(this.settings);
    next.set(id, { schema: meta.settings ?? [], own: !!meta.ownSettings });
    this.settings = next;
    if (meta.actions) { const a = new Map(this.actions); a.set(id, meta.actions); this.actions = a; }
  };

  private onTileActions = (e: Event): void => {
    const host = e.currentTarget as HTMLElement;
    const id = host.getAttribute('slot');
    if (!id) return;
    const a = new Map(this.actions);
    a.set(id, (e as CustomEvent<WidgetAction[]>).detail ?? []);
    this.actions = a;
  };

  private runTileAction(id: string, action: string): void {
    const host = this.renderRoot.querySelector(`vitesse-widget-host[slot="${id}"]`) as VitesseWidgetHost | null;
    host?.runAction(action);
  }

  /** The gear: the widget's own dialog if it has one, else the shared one over its declared schema. */
  private openTileSettings(id: string): void {
    const host = this.renderRoot.querySelector(`vitesse-widget-host[slot="${id}"]`) as VitesseWidgetHost | null;
    if (host?.openSettings()) return;
    this.settingsTile = id;
  }

  private onSettingsSave = (e: Event): void => {
    const id = this.settingsTile;
    const draft = (e as CustomEvent<Record<string, unknown>>).detail;
    this.settingsTile = '';
    if (!id || !this.body || !this.dashboards) return;
    this.saveOwn((b) => ({ ...b, tiles: b.tiles.map((tile) => (tile.id === id ? { ...tile, config: { ...tile.config, ...draft } } : tile)) }));
  };

  private onWidgetFault = (e: Event): void => {
    const host = e.target as HTMLElement;
    const id = host.getAttribute('slot');
    if (!id) return;
    const next = new Set(this.faulted);
    next.add(id);
    this.faulted = next;
  };

  /** A tile that restarted itself leaves the count; the badge is only ever what is stopped now. */
  private onWidgetRecovered = (e: Event): void => {
    const id = (e.target as HTMLElement).getAttribute('slot');
    if (!id || !this.faulted.has(id)) return;
    const next = new Set(this.faulted);
    next.delete(id);
    this.faulted = next;
  };

  protected pageTitle(): string {
    const summary = this.dashboards?.get(this.dashboardId);
    return this.body?.title ?? summary?.title ?? this.dashboardId;
  }

  protected backLabel(): string {
    return this.gatewayName || t('dashboards.back');
  }

  protected onBack(): void {
    this.dispatchEvent(
      new CustomEvent('navigate', {
        detail: { view: 'gateway', gatewayId: this.gatewayId },
        bubbles: true,
        composed: true,
      }),
    );
  }

  /** Kiosk is a wall display: the board gets the screen, and the compact bar keeps the title,
   *  the trust badges and the Exit kiosk control. */
  protected compactHeader(): boolean {
    return this.kiosk;
  }

  /**
   * A faulted tile is NAMED here rather than left as a gap in the canvas. A hole
   * in a wall display reads as "nothing is happening there", which is both the
   * wrong conclusion and the dangerous one.
   */
  /*
   * WHAT A BADGE IS FOR: telling somebody at a glance whether they can trust
   * what is on the screen.
   *
   * A tile count does not do that — the number is visible by looking. These are
   * ordered by how much they change what a person does about them: whether the
   * gateway is answering at all, then whether anything on the board is broken,
   * then what plant it covers, and only then how big it is.
   */
  protected renderActions(): unknown {
    /*
     * Only the kiosk-exit control lives here. A dashboard is not exported — widgets are DOWNLOADED
     * FROM the marketplace, not boards uploaded to it.
     *
     * EDIT carries the background picker, and this comment used to say it deliberately did not.
     * The objection then was that a background already arrived through the assistant — attached to
     * a conversation or generated inline — and that a second entry point meant a third way to set
     * one field, with its own file dialog and its own chance to skip the compression. Two thirds of
     * that is answered by construction: the dialog calls the SAME `encodeBackground` the assistant's
     * path calls, so there is still exactly one compression, and it writes an empty `model`, which
     * is already how the document spells "a person supplied this". What remained was a judgement —
     * whether somebody should have to describe a picture to a model to put it behind their own
     * board — and it went the other way. Never in kiosk, which is a wall display and shows none of
     * these controls.
     */
    if (this.kiosk)
      return button({
        label: t('dashboards.kioskExit'),
        icon: 'circle-stop',
        color: 'blue',
        size: 'sm',
        onClick: () => this.dispatchEvent(new CustomEvent('kiosk-stop', { bubbles: true, composed: true })),
      });
    // The one standing action every board has: the same gated delete the list's cards carry, so a
    // person on the board itself does not have to go back to the list to remove it.
    return html`<vitesse-gated .rpc=${Dashboards.method.save} inline
        >${button({
          label: t('dashboards.edit'),
          icon: 'pencil',
          color: 'blue',
          size: 'sm',
          onClick: () => (this.editing = true),
          disabled: !this.body,
        })}</vitesse-gated
      ><vitesse-gated .rpc=${Dashboards.method.share} inline
        >${button({
          label: t('dashboards.share'),
          icon: 'share-2',
          color: 'blue',
          size: 'sm',
          onClick: () => (this.sharing = true),
          disabled: !this.body,
        })}</vitesse-gated
      ><vitesse-gated .rpc=${Dashboards.method.delete} inline
      >${button({
        label: t('dashboards.delete'),
        icon: 'trash-2',
        color: 'red',
        size: 'sm',
        onClick: () => (this.confirmDelete = true),
        disabled: this.deleting || !this.body,
      })}</vitesse-gated
    >`;
  }

  private async runDelete(): Promise<void> {
    if (!this.dashboards || !this.dashboardId || this.deleting) return;
    this.deleting = true;
    this.deleteError = '';
    try {
      const r = await this.dashboards.delete(this.dashboardId);
      if (!r.ok) {
        this.deleteError = r.error || t('dashboards.deleteFailed');
        return;
      }
      this.confirmDelete = false;
      this.onBack(); // the board is gone; staying would show a shell of it
    } catch (e) {
      this.deleteError = e instanceof Error ? e.message : String(e);
    } finally {
      this.deleting = false;
    }
  }

  private renderShareDialog() {
    if (!this.sharing) return nothing;
    return html`<vitesse-dashboard-share-dialog
      .open=${true}
      .board=${this.body}
      .dashboards=${this.dashboards}
      .models=${this.models}
      @dialog-close=${() => (this.sharing = false)}
    ></vitesse-dashboard-share-dialog>`;
  }

  private renderDeleteDialog() {
    if (!this.confirmDelete) return nothing;
    return html`<vitesse-confirm-dialog
      .open=${true}
      heading=${t('dashboards.deleteHeading')}
      message=${t('dashboards.deleteMessage', { title: this.body?.title ?? this.dashboardId })}
      detail=${t('dashboards.deleteDetail')}
      confirm-label=${t('dashboards.delete')}
      danger
      ?busy=${this.deleting}
      error=${this.deleteError}
      @confirm=${() => void this.runDelete()}
      @dialog-close=${() => (this.confirmDelete = false)}
    ></vitesse-confirm-dialog>`;
  }

  protected renderBadges() {
    if (this.connState !== 'connected') {
      return html`${statusBadge(this.connState === 'offline' ? 'gray' : 'yellow', this.connState)}`;
    }
    /*
     * ONE badge set for EVERY board — the same slots on all of them, only the
     * values change. A person glancing at any dashboard header reads the same
     * row in the same order:
     *
     *   STOPPED — red count, only when >0: the one alarm slot ("part of this
     *   screen is not rendering").
     *   HEALTH — green LIVE once the board's values are reaching it (PLC values,
     *   or the fleet stream for a map board — native tiles feed the meter too),
     *   and it STAYS live while those values hold steady, because a reading that
     *   is not changing is not a fault; the RATE badge tells that story at 0/s.
     *   yellow STALE when tiles expect data and NONE has arrived past the grace
     *   window (a binding that is not delivering); gray "No data" while warming
     *   up or when nothing feeds it.
     *   LIVE already means the gateway is connected, so there is no separate
     *   connection badge — one green chip, not two.
     *   LATENCY — median producer-to-paint lag. "— latency" when unmeasured.
     *   RATE — updates/s over the last five seconds; 0 is a real value.
     */
    const bound = (this.body?.tiles ?? []).some(
      (x) => x.config['controllerId'] || x.config['modelId'] || x.config['integrationId'] || x.source.startsWith('native:'),
    );
    const pastGrace = Date.now() - this.openedAtMs > VitesseDashboardDetail.STALE_GRACE_MS;
    // Whether values have ever reached this board this visit — a steady reading
    // stops feeding the rate window but must not read as stale, so health keys off
    // this while the rate number reflects the (honestly 0/s) live window.
    const everLive = boardEverLive(`${this.gatewayId}/${this.dashboardId}`);
    const rate = this.metrics ? (this.metrics.perSecond >= 10 ? String(Math.round(this.metrics.perSecond)) : this.metrics.perSecond.toFixed(1)) : '0';
    const lag = this.metrics && this.metrics.lagMs > 0 ? (this.metrics.lagMs < 1000 ? `${Math.round(this.metrics.lagMs)} ms` : `${(this.metrics.lagMs / 1000).toFixed(1)} s`) : '—';
    const health = this.metrics || everLive
      ? statusBadge('green', t('dashboards.live'))
      : bound && pastGrace
        ? statusBadge('yellow', t('dashboards.stale'))
        : statusBadge('gray', t('dashboards.noData'));
    return html`${
      this.faulted.size ? statusBadge('red', t('dashboards.widgetsStopped', { count: this.faulted.size })) : nothing
    }${health}${statusBadge('gray', t('dashboards.latencyBadge', { lag }))}${statusBadge('gray', t('dashboards.rateBadge', { rate }))}`;
  }

  /*
   * THE DESCRIPTION — what this board is for, in the author's words.
   *
   * Click to edit, exactly as a controller's description works, so the gesture
   * is learned once. It differs in WHERE it lives: a controller is discovered
   * from the plant and its description is an annotation in `user_data`, while a
   * dashboard is authored, so its description travels inside the board and is
   * covered by the same digest — two people editing it collide visibly instead
   * of one silently overwriting the other.
   */
  protected renderDescription() {
    if (!this.body) return nothing;
    return html`<vitesse-editable-description
      .value=${this.body.description ?? ''}
      placeholder=${t('dashboards.descriptionFallback')}
      hint=${t('dashboards.descriptionEditHint')}
      .canEdit=${true}
      .commit=${(next: string) => this.commitDescription(next)}
    ></vitesse-editable-description>`;
  }

  private commitDescription(next: string): void {
    const board = this.body;
    if (!board) return;
    /*
     * The whole board goes back, with the digest we read it at. A save whose
     * base has moved is refused rather than merged — so a description typed
     * over a board a colleague just re-tiled is rejected with a sentence,
     * rather than quietly reverting their work.
     */
    void this.dashboards?.save({ ...board, description: next }, board.digest).then((r) => {
      if (!r.ok) return;
      this.body = { ...board, description: next };
      // The trail just gained an entry for this edit.
      this.activityRevision++;
    });
  }

  /*
   * The two questions somebody actually has in front of a board they did not
   * build, which is most boards after the week they were made.
   */
  protected aiHints(): string[] {
    /*
     * Hints follow the board's actual state, most-actionable first — a chip
     * naming a problem the board visibly has beats two generic questions.
     *
     *   unbound tiles → offer to bind them: a tile with no source renders a
     *   placeholder, and "why is this tile empty" is the question the screen
     *   is already asking.
     *   no description → offer to write one; it is the line every list scans.
     *   otherwise → explain / improve, the two questions somebody has in front
     *   of a board they did not build, which is most boards a week on.
     */
    const tiles = this.body?.tiles ?? [];
    const unbound = tiles.filter(
      // A native tile (the map) reads no symbols; counting it as unbound made
      // the hint offer to "bind" a tile that has nothing to bind.
      (x) => !x.source.startsWith('native:') && !x.config['controllerId'] && !x.config['modelId'] && !x.config['integrationId'],
    ).length;
    const hints: string[] = [];
    if (unbound > 0) hints.push(t('dashboards.hintBind', { count: unbound }));
    if (!this.body?.description) hints.push(t('dashboards.hintDescribe'));
    hints.push(t('dashboards.hintExplain'));
    if (this.body?.description && unbound === 0) hints.push(t('dashboards.hintImprove'));
    return hints.slice(0, 2); // two chips; more is a menu, not a hint
  }


  /*
   * THREE VIEWS OF ONE BOARD, and each answers a question the others cannot.
   *
   * Board is what it shows. Sources is whether it is actually reading — the
   * failure mode that renders a placeholder and looks like a dead gateway.
   * Activity is who changed it, which is the question a screen on a wall
   * provokes the moment it is not what somebody left.
   */
  protected tabs(): PillTab[] {
    return [
      { id: 'board', label: t('dashboards.board'), icon: 'layout-dashboard' },
      { id: 'activity', label: t('dashboards.logs'), icon: 'scroll-text' },
    ];
  }
  protected activeTabId(): string {
    return this.tab;
  }
  protected onTabChange(id: string): void {
    if (id !== this.tab) this.tab = id as typeof this.tab;
  }

  protected renderContent() {
    return html`${this.renderBoard()}${this.renderDeleteDialog()}${this.renderShareDialog()}
      <vitesse-edit-dashboard-dialog
        .open=${this.editing}
        .dashboards=${this.dashboards}
        .board=${this.body}
        @dialog-close=${() => (this.editing = false)}
      ></vitesse-edit-dashboard-dialog>`;
  }

  private renderBoard() {
    // Checked BEFORE anything else, including the body: tiles are not mounted
    // at all while the gateway is away, so no frame, worker or value stream is
    // started against a connection that cannot answer. Coming back re-renders
    // and they mount normally.
    if (this.connState !== 'connected') {
      return html`<div class="dashboard-empty">${
        this.connState === 'offline' ? t('dashboards.gatewayOffline')
        : this.connState === 'connecting' ? t('dashboards.gatewayConnecting')
        : t('dashboards.gatewayDisconnected')
      }</div>`;
    }
    if (this.missing) {
      return html`<div class="dashboard-empty">${t('dashboards.notFound')}</div>`;
    }
    if (!this.body) {
      return html`<div class="dashboard-empty">${t('common.loading')}</div>`;
    }
    /*
     * The tabs are checked BEFORE the empty-board case: a board with no tiles
     * still has an activity trail worth reading — very likely the one that says
     * who removed them.
     */
    if (this.tab === 'activity') {
      return html`<vitesse-dashboard-activity
        .backend=${this.backend}
        .gatewayId=${this.gatewayId}
        .dashboardId=${this.dashboardId}
        .revision=${this.activityRevision}
        .selfId=${this.selfId}
        .selfName=${this.selfName}
      ></vitesse-dashboard-activity>`;
    }
    /*
     * A BOARD WITH A BACKGROUND AND NO TILES IS STILL A BOARD WITH A BACKGROUND.
     *
     * The empty state used to return before anything else could draw, so setting a
     * picture on a board you had not yet filled showed nothing and read as the
     * save having failed. Wrapped in the stage, the picture is there and the
     * "add some tiles" line sits on top of it — which is also the order somebody
     * building a board actually works in.
     */
    if (this.body.tiles.length === 0) {
      return this.stage(html`<div class="dashboard-empty">${t('dashboards.empty')}</div>`);
    }
    // KEYED ON THE DASHBOARD. The panel grid builds its items once, in
    // firstUpdated, and reconciles only visibility afterwards - it is built for
    // a fixed set of panels that the user shows and hides, which is what the
    // model workspace has. A dashboard is not that: navigating to another one
    // replaces the panel set wholesale, and without a key Lit reuses the same
    // grid element, so the previous board's items stay on screen while the new
    // board's tiles have no slot to render into. That is the "I click through
    // and the next one is empty" failure exactly.
    //
    // Keying makes the grid a new element per dashboard, which is also what
    // guarantees the old tiles are DISCONNECTED - and disconnecting is what
    // releases their value subscriptions, so leaving a board stops its data.
    return this.stage(html`<div class="vg-wrapper dashboard-canvas">
      ${keyed(this.body.id, html`<vitesse-panel-grid
        .panels=${this.panels}
        .layout=${this.layout}
        @layout-change=${this.onLayoutChange}
        @panel-remove=${this.onPanelRemove}
      >
        ${repeat(
          this.body.tiles,
          (tile) => tile.id,
          (tile) => html`<vitesse-widget-host
            slot=${tile.id}
            .source=${tile.source}
            .config=${tile.config}
            .grant=${this.grant}
            .backend=${this.backend}
            .gatewayId=${this.gatewayId}
            .boardKey=${this.dashboardId}
            .mapFleet=${this.mapFleet}
            .mapServers=${this.mapServers}
            .orgNames=${this.orgNames}
            .models=${this.models}
            @widget-ready=${this.onWidgetReady}
            @tile-actions=${this.onTileActions}
            @widget-fault=${this.onWidgetFault}
            @widget-recovered=${this.onWidgetRecovered}
            @tile-config-change=${this.onTileConfigChange}
            @tile-title-change=${this.onTileTitleChange}
          ></vitesse-widget-host>${this.gearFor(tile)}`,
        )}
      </vitesse-panel-grid>`)}
      ${this.settingsDialog()}
    </div>`);
  }

  /** The base widget's gear, slotted into the tile bar beside the trash — only when the widget declared settings. */
  private gearFor(tile: DashboardTile) {
    const s = this.settings.get(tile.id);
    const acts = this.actions.get(tile.id) ?? [];
    const gear = s && (s.own || s.schema.length > 0);
    // Publish: only for a widget that is on no shelf yet — a built-in, a marketplace widget or one
    // already published has nothing to publish. A change to a published widget's source makes it a
    // new widget, and the button comes back.
    const publish = !this.kiosk && !!this.knownSource && !tile.source.startsWith('native:') && !this.knownSource(tile.source);
    if (!gear && !acts.length && !publish) return nothing;
    return html`<span slot=${`${tile.id}-controls`} class="tile-controls">
      ${acts.map((a) => iconButton({ icon: a.icon, title: a.title, size: 18, active: !!a.active, onClick: () => this.runTileAction(tile.id, a.id) }))}
      ${publish ? iconButton({ icon: 'layer-arrow-up', title: t('dashboards.publishWidget'), size: 18, onClick: () => this.dispatchEvent(new CustomEvent<TilePublish>('tile-publish', { detail: { tile, meta: this.metas.get(tile.id) }, bubbles: true, composed: true })) }) : nothing}
      ${gear ? iconButton({ icon: 'settings', title: t('dashboards.tileSettings'), size: 18, onClick: () => this.openTileSettings(tile.id) }) : nothing}
    </span>`;
  }

  private settingsDialog() {
    const tile = this.body?.tiles.find((x) => x.id === this.settingsTile);
    const s = tile ? this.settings.get(tile.id) : undefined;
    return html`<vitesse-widget-config-dialog
      .open=${!!tile}
      .heading=${tile?.title ?? ''}
      .schema=${s?.schema ?? []}
      .config=${tile?.config ?? {}}
      .controllers=${(this.controllers?.all ?? []).map((c) => ({ value: c.id, label: c.name || c.id }))}
      @settings-save=${this.onSettingsSave}
      @settings-close=${() => { this.settingsTile = ''; }}
    ></vitesse-widget-config-dialog>`;
  }

  /**
   * Put the board's content on its stage — with the picture behind it, or
   * without, which is the same markup minus one element.
   *
   * A single wrapper for both the canvas and the empty state, so a background
   * cannot render for one and not the other; and the picture is a SIBLING of the
   * content rather than a background-image on the wrapper, because it carries a
   * scrim and one element gives one layer.
   */
  private stage(content: unknown) {
    const style = this.backgroundStyle();
    if (style === nothing) return html`${content}`;
    return html`<div class="dashboard-stage" style=${style}>
      <div class="dashboard-stage-bg"></div>
      ${content}
    </div>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'vitesse-dashboard-detail': VitesseDashboardDetail;
  }
}
